/**
 * 凭证 builder 注册表
 *
 * 设计来源：doc/反恐舱单优化体验/设计文档_工具化体验优化.md §2.5 / §7.6
 * - 各产品 builder 在各自文件内注册（见 build/index.ts 的副作用引入）
 * - 未注册的产品直接抛错，不返回假数据（禁止伪造实现）
 */
import type {
  CertificateBuilder,
  CertificateContext,
  CertificateData,
  CertificateProduct,
} from '../types'

/** 凭证标题（固定英文） */
export const CERTIFICATE_TITLES: Record<CertificateProduct, string> = {
  AMS: 'AMS FILING CERTIFICATE',
  ISF: 'ISF FILING CERTIFICATE',
  ACI: 'ACI FILING CERTIFICATE',
  EMF: 'EMANIFEST FILING CERTIFICATE',
  ICS2: 'ENS FILING CERTIFICATE',
  AFR: 'AFR FILING CERTIFICATE',
  MPCI: 'MPCI FILING CERTIFICATE',
}

const builders = new Map<CertificateProduct, CertificateBuilder>()

export function registerCertificateBuilder(
  product: CertificateProduct,
  builder: CertificateBuilder
): void {
  builders.set(product, builder)
}

export function isCertificateBuilderRegistered(product: CertificateProduct): boolean {
  return builders.has(product)
}

export function buildCertificateData(
  product: CertificateProduct,
  context: CertificateContext
): CertificateData {
  const builder = builders.get(product)
  if (!builder) {
    throw new Error(`[cw-shared] no certificate builder registered for product: ${product}`)
  }
  return builder(context)
}

/** 提单号候选字段，hbl 优先（与原型一致） */
const BILL_NO_KEYS = ['hblNo', 'hbl', 'houseBillNo', 'mblNo', 'mbl', 'masterBillNo'] as const

function pickString(source: Record<string, unknown> | undefined, keys: readonly string[]): string {
  if (!source) return ''
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

export function certificateFilename(
  product: CertificateProduct,
  context: CertificateContext
): string {
  const billNo = pickString(context.listRow ?? context.detail, BILL_NO_KEYS)
  const safeBillNo = (billNo || 'certificate').replace(/[\\/:*?"<>|]/g, '_')
  return `${product}_FILING_CERTIFICATE_${safeBillNo}.pdf`
}
